'use client';

import { useMemo, useCallback, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@/lib/table-features';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  useQueryState
} from 'nuqs';
import type { ExtendedColumnSort } from '@/types/data-table';
import { getSortingStateParser } from '@/lib/parsers';
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  EllipsisVertical,
  ExternalLink,
  Eye,
  FileStack,
  FileText,
  FlaskConical,
  History,
  LifeBuoy,
  Loader2,
  Pause,
  PauseCircle,
  Play,
  ScanText,
  Trash2,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from '@/i18n/routing';

import { useDataTable } from '@/hooks/use-data-table';
import { useFilteredColumns } from '@/hooks/use-filtered-columns';
import { useHashDialog, useHashParamDialog } from '@/hooks/use-hash-dialog';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { ListingSkeleton } from '@/components/listing-skeleton';
import { DataTable } from '@/components/ui/table/data-table';
import { DataTableToolbar } from '@/components/ui/table/data-table-toolbar';
import { DataTableColumnHeader } from '@/components/ui/table/data-table-column-header';
import { DataViewToggle, useDataView } from '@/components/data-view-toggle';
import { PaginationControls } from '@/components/pagination-controls';
import { SortControl } from '@/components/sort-control';
import {
  HighlightMatch,
  getColumnSearchTerms
} from '@/components/highlight-match';
import { PermissionGate } from '@/features/auth';
import { RouteGate } from '@/components/route-gate';
import { BatchPdfViewer } from '@/features/photo-scanning';
import {
  MoveToRepairDialog,
  type MoveToRepairPayload
} from '@/features/repair-settings';
import { ocrTemplatesApi, TemplateFieldsInfo } from '@/features/ocr-templates';
import { useInfiniteSelect } from '@/hooks/use-infinite-select';
import { useAuthenticatedBlobUrl } from '@/hooks/use-authenticated-blob-url';
import { API_ENDPOINTS } from '@/lib/api/config';
import { OCR_HASH } from '../constants';
import { ocrQueries } from '../services/ocr.queries';
import {
  useStartOcrMutation,
  useRefuseOcrContainerMutation,
  useDeleteTestContainerMutation,
  usePauseOcrContainerMutation,
  useResumeOcrContainerMutation,
  useRecoverOcrContainerMutation
} from '../services/ocr.mutations';
import {
  parseResultsViewParams,
  resultsViewParams,
  type ResultsDeepLink
} from '../utils/results-deep-link';
import { ContainerResultsSheet } from './container-results-sheet';
import { ContainerResultsOverlay } from './container-results-overlay';
import { ContainerCard } from './container-card';
import { ContainerCompactRow } from './container-compact-row';
import {
  ContainerRepairHistoryDialog,
  REPAIR_STATUS_STYLES
} from './container-repair-history-dialog';
import { CreateTestContainerDialog } from './create-test-container-dialog';
import { OcrTemplatePoolDialog } from './ocr-template-pool-dialog';
import { isInOcrQa } from './ocr-qa-stage-pill';
import { ContainerIdPrefix } from './container-id-prefix';
import type { OcrContainersListParams } from '../services/ocr.api';
import type { OcrContainer, OcrResult } from '../types';
import { resolveContainerTemplate } from '../utils/container-template';
import { resolveOcrContainerLabel } from '../utils/container-label';
import { StagePill, SubStatusCell } from '@/features/registration';

const OCR_STATUS_STYLES: Record<
  string,
  { className: string; icon: typeof CheckCircle }
> = {
  processing: {
    className:
      'border-blue-500/30 bg-blue-500/10 text-blue-600 dark:text-blue-400',
    icon: Loader2
  },
  paused: {
    className:
      'border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400',
    icon: PauseCircle
  },
  completed: {
    className:
      'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    icon: CheckCircle
  },
  failed: {
    className: 'border-red-500/30 bg-red-500/10 text-red-600 dark:text-red-400',
    icon: XCircle
  }
};

// Container-level QA lifecycle status (distinct from ocr_status): tracks where a
// container sits across the QA1/QA2/QA3 tiers, including reject round-trips.
const REVIEW_STATUS_STYLES: Record<
  string,
  { className: string; icon: typeof CheckCircle }
> = {
  not_in_qa: {
    className:
      'border-slate-500/30 bg-slate-500/10 text-slate-600 dark:text-slate-400',
    icon: Clock
  },
  in_qa1: {
    className:
      'border-blue-500/30 bg-blue-500/10 text-blue-600 dark:text-blue-400',
    icon: Eye
  },
  in_qa2: {
    className:
      'border-indigo-500/30 bg-indigo-500/10 text-indigo-600 dark:text-indigo-400',
    icon: Eye
  },
  in_qa3: {
    className:
      'border-violet-500/30 bg-violet-500/10 text-violet-600 dark:text-violet-400',
    icon: Eye
  },
  returned_to_ocr: {
    className:
      'border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400',
    icon: AlertTriangle
  },
  completed: {
    className:
      'border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    icon: CheckCircle
  }
};

interface OcrContainersListingProps {
  /**
   * Per-page slot for the in-screen results overlay, handed to the extraction
   * panel unchanged.
   *
   * Plumbed from the app layer rather than imported here because the only
   * current filler — the QA rejection reasons — lives in `features/ocr-qa`,
   * which already imports FROM this feature. Reaching back for it would close
   * that loop; the page, belonging to neither feature, can hold both ends.
   * Keeping it means a container QA bounced back shows the reviewer's reasons
   * on the screen the operator fixes it from, not only on the standalone route.
   */
  renderResultsPageFooter?: (
    result: OcrResult,
    pageIndexInResult: number,
    isFirstGroupForResult: boolean
  ) => ReactNode;
}

export function OcrContainersListing({
  renderResultsPageFooter
}: OcrContainersListingProps = {}) {
  const t = useTranslations('ocr');
  const tReg = useTranslations('registration');
  const tRepair = useTranslations('repairs');
  const [view, setView] = useDataView('ocr-containers');
  // Overlay open-state lives in the URL hash so overlays are deep-linkable,
  // survive refresh, and close on browser Back. Row-scoped overlays carry the
  // entity id (container id, or batch id for the PDF viewer) as the hash param;
  // the rest of each target is resolved from the already-loaded row list.
  // Destructure the stable callbacks so they can go in hook dep arrays without
  // tripping the React Compiler's manual-memoization check.
  const { open: createTestOpen, onOpenChange: toggleCreateTest } =
    useHashDialog(OCR_HASH.createTest);
  const {
    open: poolOpen,
    param: poolContainerId,
    openWith: openPool,
    onOpenChange: closePool
  } = useHashParamDialog(OCR_HASH.templatePool);
  const {
    open: repairHistoryOpen,
    param: repairHistoryId,
    openWith: openRepairHistory,
    onOpenChange: closeRepairHistory
  } = useHashParamDialog(OCR_HASH.repairHistory);
  const {
    open: resultsOpen,
    param: resultsContainerId,
    openWith: openResults,
    onOpenChange: closeResults
  } = useHashParamDialog(OCR_HASH.results);
  const {
    open: resultsViewOpen,
    params: resultsViewSegments,
    openWith: openResultsView,
    onOpenChange: closeResultsView
  } = useHashParamDialog(OCR_HASH.resultsView);
  const {
    open: pdfOpen,
    param: pdfBatchParam,
    openWith: openPdf,
    onOpenChange: closePdf
  } = useHashParamDialog(OCR_HASH.pdf);
  const {
    open: refuseOpen,
    param: refuseId,
    openWith: openRefuse,
    onOpenChange: closeRefuse
  } = useHashParamDialog(OCR_HASH.moveToRepair);
  const {
    open: deleteTestOpen,
    param: deleteTestId,
    openWith: openDeleteTest,
    onOpenChange: closeDeleteTest
  } = useHashParamDialog(OCR_HASH.deleteTest);

  // The PDF is streamed through the authenticated batch endpoint (keyed by
  // batch id) so the preview works even when the API returns no signed
  // pdf_url (local / non-public disks). BatchPdfViewer serves the mobile
  // page-image fallback off the same batch id.
  const pdfBatchId = pdfOpen && pdfBatchParam ? Number(pdfBatchParam) : null;
  const pdfBlobUrl = useAuthenticatedBlobUrl(
    pdfBatchId ? API_ENDPOINTS.PHOTO_SCANNING.BATCH_PDF(pdfBatchId) : null
  );

  const startMutation = useStartOcrMutation();
  const refuseMutation = useRefuseOcrContainerMutation();
  const deleteTestMutation = useDeleteTestContainerMutation();
  const pauseMutation = usePauseOcrContainerMutation();
  const resumeMutation = useResumeOcrContainerMutation();
  const recoverMutation = useRecoverOcrContainerMutation();

  const handleDeleteTest = useCallback(
    (container: OcrContainer) => openDeleteTest(String(container.id)),
    [openDeleteTest]
  );
  const handleViewRepairHistory = useCallback(
    (container: OcrContainer) => openRepairHistory(String(container.id)),
    [openRepairHistory]
  );

  const [urlPage] = useQueryState('page', parseAsInteger.withDefault(1));
  const [urlPerPage] = useQueryState('perPage', parseAsInteger.withDefault(10));
  const [urlSearch] = useQueryState(
    'container_number',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlTemplateId] = useQueryState(
    'ocr_template_id',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlOcrStatus] = useQueryState(
    'ocr_status',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlCreatedAt] = useQueryState(
    'created_at',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlTotalPages] = useQueryState(
    'total_pages',
    parseAsString.withDefault('')
  );
  const [urlRepairStatus] = useQueryState(
    'repair_status',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlRepairCount] = useQueryState(
    'repair_count',
    parseAsString.withDefault('')
  );
  const [urlReviewStatus] = useQueryState(
    'review_status',
    parseAsArrayOf(parseAsString, ',').withDefault([])
  );
  const [urlReviewCount] = useQueryState(
    'review_count',
    parseAsString.withDefault('')
  );
  const [urlSorting] = useQueryState(
    'sort',
    getSortingStateParser<OcrContainer>().withDefault([
      { id: 'created_at', desc: true }
    ] as ExtendedColumnSort<OcrContainer>[])
  );

  const handleViewPdf = useCallback(
    (container: OcrContainer) => {
      const batch = container.latest_batch;
      if (!batch?.id || !batch.pdf_path) {
        toast.error(t('pdfNotReady'));
        return;
      }
      openPdf(String(batch.id));
    },
    [t, openPdf]
  );

  /**
   * "View results" — the page-by-page summary sheet. Every view offers it from
   * the overflow menu; the primary action beside it opens the standalone
   * results page, which is what deep links and bookmarks point at.
   */
  const handleOpenResultsSummary = useCallback(
    (container: OcrContainer) => openResults(String(container.id)),
    [openResults]
  );

  /**
   * The summary sheet's own rows, opening the panel in-screen ON a page or a
   * field. This replaces the `<Link>` to the standalone route those rows used
   * to carry — the same target, delivered without leaving the screen.
   */
  const handleOpenResultsView = useCallback(
    (containerId: number, target?: ResultsDeepLink) =>
      openResultsView(...resultsViewParams(containerId, target)),
    [openResultsView]
  );

  const handleStartOcr = useCallback(
    (container: OcrContainer) => {
      const registrationId = container.registration?.id;
      if (registrationId) {
        openPool(String(container.id));
        return;
      }
      // No registration linked — template suggestions need one, so keep the
      // legacy direct start.
      startMutation.mutateAsync({ containerId: container.id }).catch(() => {
        // handled by global MutationCache
      });
    },
    [startMutation, openPool]
  );

  const handleMoveToRepair = useCallback(
    (container: OcrContainer) => openRefuse(String(container.id)),
    [openRefuse]
  );

  // Pause/resume act on the whole container: a run is one OcrResult per
  // candidate template, and stopping only some of them would leave the
  // container half-extracted.
  const handlePauseOcr = useCallback(
    (container: OcrContainer) => {
      pauseMutation.mutateAsync({ containerId: container.id }).catch(() => {
        // handled by global MutationCache
      });
    },
    [pauseMutation]
  );

  const handleResumeOcr = useCallback(
    (container: OcrContainer) => {
      resumeMutation.mutateAsync({ containerId: container.id }).catch(() => {
        // handled by global MutationCache
      });
    },
    [resumeMutation]
  );

  // Escape hatch for a container that has stopped moving. Recovery is
  // automatic and in band; this is "do it now", and it is reversible — the
  // pages that never returned come back as failed ranges the operator can
  // retry.
  const handleRecoverOcr = useCallback(
    (container: OcrContainer) => {
      recoverMutation.mutateAsync({ containerId: container.id }).catch(() => {
        // handled by global MutationCache
      });
    },
    [recoverMutation]
  );

  const templateSelect = useInfiniteSelect({
    queryKey: ['ocr-templates'],
    queryFn: (params) => ocrTemplatesApi.getAll({ ...params, is_active: true }),
    mapOption: useCallback(
      (tpl: { id: number; name: string }) => ({
        label: tpl.name,
        value: String(tpl.id)
      }),
      []
    )
  });

  const queryParams = useMemo<OcrContainersListParams>(() => {
    const params: OcrContainersListParams = {
      page: urlPage,
      per_page: urlPerPage
    };
    if (urlSearch.length) params.search = urlSearch.join(',');
    if (urlTemplateId.length) params.ocr_template_id = urlTemplateId;
    if (urlOcrStatus.length) params.ocr_status = urlOcrStatus;
    if (urlCreatedAt.length) {
      const t0 = Number(urlCreatedAt[0]);
      const t1 = Number(urlCreatedAt[1]);
      if (t0) params.date_from = new Date(t0).toISOString().split('T')[0];
      if (t1) params.date_to = new Date(t1).toISOString().split('T')[0];
    }
    if (urlTotalPages) {
      const parts = String(urlTotalPages)
        .split(',')
        .map(Number)
        .filter((n) => !isNaN(n));
      if (parts.length >= 1) params.min_pages = parts[0];
      if (parts.length >= 2) params.max_pages = parts[1];
    }
    if (urlRepairStatus.length) params.repair_status = urlRepairStatus;
    if (urlRepairCount) {
      const parts = String(urlRepairCount)
        .split(',')
        .map(Number)
        .filter((n) => !isNaN(n));
      if (parts.length >= 1) params.min_repair_count = parts[0];
      if (parts.length >= 2) params.max_repair_count = parts[1];
    }
    if (urlReviewStatus.length) params.review_status = urlReviewStatus;
    if (urlReviewCount) {
      const parts = String(urlReviewCount)
        .split(',')
        .map(Number)
        .filter((n) => !isNaN(n));
      if (parts.length >= 1) params.min_review_count = parts[0];
      if (parts.length >= 2) params.max_review_count = parts[1];
    }
    params.sort_by = urlSorting[0]?.id ?? 'created_at';
    params.sort_direction = urlSorting[0]?.desc ? 'desc' : 'asc';
    return params;
  }, [
    urlPage,
    urlPerPage,
    urlSearch,
    urlTemplateId,
    urlOcrStatus,
    urlCreatedAt,
    urlTotalPages,
    urlRepairStatus,
    urlRepairCount,
    urlReviewStatus,
    urlReviewCount,
    urlSorting
  ]);

  const { data, isPending, isPlaceholderData } = useQuery(
    ocrQueries.containers(queryParams)
  );

  const containers = useMemo(
    () => (data?.data ?? []) as OcrContainer[],
    [data]
  );
  const pageCount = data?.pagination?.totalPages ?? 0;
  const pagination = data?.pagination;

  // Resolve each hash id param back to the loaded row (the row must be on the
  // current page; deep-linking an off-page id simply won't open the overlay —
  // no extra fetch is added). The container label is derived off the resolved
  // row so it survives a hash-only open.
  const containerLabel = (container: OcrContainer | null | undefined) =>
    resolveOcrContainerLabel(
      {
        is_test: container?.is_test,
        container_number: container?.registration?.container_number,
        entity_name: container?.entity_name
      },
      t('untitledTestDocument')
    );

  const poolTargetContainer = poolOpen
    ? (containers.find((c) => String(c.id) === poolContainerId) ?? null)
    : null;
  const poolTarget =
    poolTargetContainer && poolTargetContainer.registration?.id
      ? {
          containerId: poolTargetContainer.id,
          registrationId: poolTargetContainer.registration.id,
          label: containerLabel(poolTargetContainer),
          batchId: poolTargetContainer.latest_batch?.id ?? null
        }
      : null;

  const repairHistoryTarget = repairHistoryOpen
    ? (containers.find((c) => String(c.id) === repairHistoryId) ?? null)
    : null;
  const resultsTarget = resultsOpen
    ? (containers.find((c) => String(c.id) === resultsContainerId) ?? null)
    : null;
  // The in-screen results overlay carries BOTH halves of a destination in its
  // params — which container, and (when the summary sheet handed one over)
  // which page or field to land on. Unlike every other overlay here the
  // container id is used directly rather than resolved against the loaded page
  // of rows: the panel fetches the container's results itself, so the overlay
  // must still open for a row that is not on the current page.
  const resultsViewTarget = resultsViewOpen
    ? parseResultsViewParams(resultsViewSegments)
    : null;
  const resultsViewContainer = resultsViewTarget
    ? (containers.find((c) => c.id === resultsViewTarget.containerId) ?? null)
    : null;
  const refuseTarget = refuseOpen
    ? (containers.find((c) => String(c.id) === refuseId) ?? null)
    : null;
  const deleteTestTarget = deleteTestOpen
    ? (containers.find((c) => String(c.id) === deleteTestId) ?? null)
    : null;
  // The PDF viewer is keyed by batch id, so resolve the owning container by
  // its latest batch to recover the header title.
  const pdfContainer =
    pdfBatchId != null
      ? (containers.find((c) => c.latest_batch?.id === pdfBatchId) ?? null)
      : null;
  const pdfTitle = pdfContainer ? containerLabel(pdfContainer) : '';

  const handleConfirmRefuse = (data: MoveToRepairPayload) => {
    if (!refuseTarget?.registration?.id) return;
    refuseMutation.mutate(
      { containerId: refuseTarget.id, ...data },
      { onSuccess: () => closeRefuse(false) }
    );
  };

  async function confirmDeleteTest() {
    if (!deleteTestTarget) return;
    try {
      await deleteTestMutation.mutateAsync(deleteTestTarget.id);
    } catch {
      // handled by global MutationCache
    }
    closeDeleteTest(false);
  }

  const columns = useMemo<ColumnDef<OcrContainer>[]>(
    () => [
      {
        id: 'container_number',
        accessorKey: 'container_number',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('containerId')} />
        ),
        cell: ({ row, column }) => {
          // A test container is named after the file the operator uploaded,
          // never after its throwaway `OCR-TEST-…` registration number.
          const value = resolveOcrContainerLabel(
            {
              is_test: row.original.is_test,
              container_number: row.original.registration?.container_number,
              entity_name: row.original.entity_name
            },
            t('untitledTestDocument')
          );
          return (
            <span
              className='flex items-center gap-1.5 font-medium'
              title={value}
            >
              <ContainerIdPrefix id={row.original.id} />
              <span dir='auto'>
                <HighlightMatch
                  text={value}
                  query={getColumnSearchTerms(column)}
                />
              </span>
              {row.original.is_test && (
                <Badge
                  variant='outline'
                  className='border-purple-500/30 bg-purple-500/10 text-[10px] text-purple-600 dark:text-purple-400'
                >
                  {t('testBadge')}
                </Badge>
              )}
            </span>
          );
        },
        enableColumnFilter: true,
        meta: {
          label: t('searchContainers'),
          variant: 'text' as const,
          placeholder: t('searchContainers'),
          permission: 'ocr.column.containerId'
        }
      },
      {
        id: 'ocr_template_id',
        accessorFn: (row) => {
          const { template } = resolveContainerTemplate(row);
          return template ? String(template.id) : '';
        },
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('template')} />
        ),
        cell: ({ row }) => {
          const { template, extra } = resolveContainerTemplate(row.original);
          return (
            <span className='text-muted-foreground flex items-center gap-1.5 text-sm'>
              <span className='truncate'>{template?.name ?? '-'}</span>
              {extra > 0 && <span className='shrink-0'>+{extra}</span>}
              <TemplateFieldsInfo templateId={template?.id ?? null} />
            </span>
          );
        },
        enableColumnFilter: true,
        meta: {
          label: t('template'),
          variant: 'infiniteMultiSelect' as const
        }
      },
      // {
      //   id: 'current_stage',
      //   header: ({ column }) => (
      //     <DataTableColumnHeader column={column} title={tReg('currentStage')} />
      //   ),
      //   cell: ({ row }) => {
      //     const stage = row.original.registration?.current_stage;
      //     if (!stage) {
      //       return (
      //         <span className='text-muted-foreground text-xs'>&mdash;</span>
      //       );
      //     }
      //     const camel = stage
      //       .split('_')
      //       .map((p, i) =>
      //         i === 0 ? p : p.charAt(0).toUpperCase() + p.slice(1)
      //       )
      //       .join('');
      //     const pascal = camel.charAt(0).toUpperCase() + camel.slice(1);
      //     return (
      //       <StagePill stage={stage} label={tReg(`stage${pascal}` as never)} />
      //     );
      //   },
      //   enableSorting: false,
      //   meta: {
      //     label: tReg('currentStage'),
      //     permission: 'ocr.column.currentStage'
      //   }
      // },
      // {
      //   id: 'subStatus',
      //   header: ({ column }) => (
      //     <DataTableColumnHeader column={column} title={tReg('subStatus')} />
      //   ),
      //   cell: ({ row }) => (
      //     <SubStatusCell
      //       stageStatus={row.original.registration?.stage_status}
      //       physicalStage={row.original.registration?.physical_stage}
      //     />
      //   ),
      //   enableSorting: false,
      //   meta: { label: tReg('subStatus'), permission: 'ocr.column.subStatus' }
      // },
      {
        id: 'ocr_status',
        accessorKey: 'ocr_status',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('statusCol')} />
        ),
        cell: ({ row }) => {
          // Always the raw OCR status — the QA tier has its own `review_status`
          // column, and swapping it in here broke this column's filter (a
          // "completed" match rendered as a QA-stage pill) and diverged for
          // test containers, which never advance `current_stage`.
          const status = row.original.ocr_status;
          if (!status) {
            return (
              <span className='text-muted-foreground text-xs'>&mdash;</span>
            );
          }
          const style = OCR_STATUS_STYLES[status];
          const Icon = style?.icon ?? Clock;
          // No `pending` arm: the backend folds pending into `processing`
          // because the distinction (queued vs. claimed by a worker) is
          // meaningless to an operator. See OcrService::listContainers.
          const label =
            status === 'processing'
              ? t('statusProcessing')
              : status === 'paused'
                ? t('statusPaused')
                : status === 'completed'
                  ? t('statusCompleted')
                  : status === 'failed'
                    ? t('statusFailed')
                    : status;
          return (
            <Badge variant='outline' className={cn('gap-1', style?.className)}>
              <Icon
                className={cn(
                  'h-3 w-3',
                  status === 'processing' && 'animate-spin'
                )}
              />
              {label}
            </Badge>
          );
        },
        enableSorting: false,
        enableColumnFilter: true,
        meta: {
          label: t('statusCol'),
          variant: 'multiSelect' as const,
          options: [
            // `processing` matches pending rows too — the backend widens it to
            // both statuses so this single option can't miss half the queue.
            { label: t('statusProcessing'), value: 'processing' },
            { label: t('statusPaused'), value: 'paused' },
            { label: t('statusCompleted'), value: 'completed' },
            { label: t('statusFailed'), value: 'failed' }
          ]
        }
      },
      {
        id: 'created_at',
        accessorKey: 'created_at',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('date')} />
        ),
        cell: ({ row }) => (
          <span className='text-muted-foreground text-sm'>
            {new Date(row.original.created_at).toLocaleDateString('en-US', {
              day: '2-digit',
              month: 'short',
              year: '2-digit'
            })}
          </span>
        ),
        enableColumnFilter: true,
        meta: { label: t('date'), variant: 'dateRange' as const }
      },
      {
        id: 'total_pages',
        accessorKey: 'total_pages',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('totalPages')} />
        ),
        cell: ({ row }) => (
          <span className='text-muted-foreground text-sm'>
            {row.original.total_pages ?? 0} {t('pages')}
          </span>
        ),
        enableSorting: false,
        enableColumnFilter: true,
        meta: {
          label: t('totalPages'),
          variant: 'range' as const,
          range: [0, 500] as [number, number],
          unit: t('pages'),
          permission: 'ocr.column.totalPages'
        }
      },
      {
        id: 'repair_status',
        accessorFn: (row) =>
          row.registration?.latest_container_transfer?.repair_status ?? 'never',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('repairStatusCol')} />
        ),
        cell: ({ row }) => {
          const status =
            row.original.registration?.latest_container_transfer?.repair_status;
          if (!status) {
            return (
              <Badge variant='outline' className='text-muted-foreground gap-1'>
                {t('repairStatusNever')}
              </Badge>
            );
          }
          const style = REPAIR_STATUS_STYLES[status];
          const Icon = style.icon;
          const label =
            status === 'waiting'
              ? t('repairStatusWaiting')
              : status === 'completed'
                ? t('repairStatusCompleted')
                : t('repairStatusReturned');
          return (
            <Badge variant='outline' className={cn('gap-1', style.className)}>
              <Icon className='h-3 w-3' />
              {label}
            </Badge>
          );
        },
        enableSorting: false,
        enableColumnFilter: true,
        meta: {
          label: t('repairStatusCol'),
          variant: 'multiSelect' as const,
          options: [
            { label: t('repairStatusNever'), value: 'never' },
            { label: t('repairStatusWaiting'), value: 'waiting' },
            { label: t('repairStatusCompleted'), value: 'completed' },
            { label: t('repairStatusReturned'), value: 'returned' }
          ],
          permission: 'ocr.column.repairStatus'
        }
      },
      {
        id: 'repair_count',
        accessorKey: 'repair_count',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('repairCount')} />
        ),
        cell: ({ row }) => (
          <span className='text-muted-foreground text-sm'>
            {row.original.repair_count ?? 0}
          </span>
        ),
        enableSorting: false,
        enableColumnFilter: true,
        meta: {
          label: t('repairCount'),
          variant: 'range' as const,
          range: [0, 50] as [number, number],
          unit: t('times'),
          permission: 'ocr.column.repairCount'
        }
      },
      {
        id: 'review_status',
        accessorFn: (row) => row.review_status ?? 'not_in_qa',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('reviewStatusCol')} />
        ),
        cell: ({ row }) => {
          const status = row.original.review_status ?? 'not_in_qa';
          const style = REVIEW_STATUS_STYLES[status];
          const Icon = style?.icon ?? Clock;
          const label =
            status === 'in_qa1'
              ? t('reviewStatusInQa1')
              : status === 'in_qa2'
                ? t('reviewStatusInQa2')
                : status === 'in_qa3'
                  ? t('reviewStatusInQa3')
                  : status === 'returned_to_ocr'
                    ? t('reviewStatusReturnedToOcr')
                    : status === 'completed'
                      ? t('reviewStatusCompleted')
                      : t('reviewStatusNotInQa');
          return (
            <Badge variant='outline' className={cn('gap-1', style?.className)}>
              <Icon className='h-3 w-3' />
              {label}
            </Badge>
          );
        },
        enableSorting: false,
        enableColumnFilter: true,
        meta: {
          label: t('reviewStatusCol'),
          variant: 'multiSelect' as const,
          options: [
            { label: t('reviewStatusNotInQa'), value: 'not_in_qa' },
            { label: t('reviewStatusInQa1'), value: 'in_qa1' },
            { label: t('reviewStatusInQa2'), value: 'in_qa2' },
            { label: t('reviewStatusInQa3'), value: 'in_qa3' },
            { label: t('reviewStatusReturnedToOcr'), value: 'returned_to_ocr' },
            { label: t('reviewStatusCompleted'), value: 'completed' }
          ],
          permission: 'ocr.column.reviewStatus'
        }
      },
      {
        id: 'review_count',
        accessorKey: 'review_count',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title={t('reviewCount')} />
        ),
        cell: ({ row }) => (
          <span className='text-muted-foreground text-sm'>
            {row.original.review_count ?? 0}
          </span>
        ),
        enableSorting: false,
        enableColumnFilter: true,
        meta: {
          label: t('reviewCount'),
          variant: 'range' as const,
          range: [0, 50] as [number, number],
          unit: t('times'),
          permission: 'ocr.column.reviewCount'
        }
      },
      {
        id: 'actions',
        header: () => <div className='text-end'>{t('actionsCol')}</div>,
        cell: ({ row }) => {
          const container = row.original;
          const status = container.ocr_status;
          const hasPdf = !!container.latest_batch?.pdf_path;

          return (
            <div className='flex items-center justify-end gap-1'>
              {/* Primary: context-aware action */}
              {!status ? (
                <PermissionGate permissions={['ocr.edit']}>
                  <Button
                    onClick={() => handleStartOcr(container)}
                    disabled={startMutation.isPending}
                    variant='outline'
                    size='sm'
                    className='h-8'
                  >
                    {startMutation.isPending ? (
                      <Loader2 className='me-1.5 h-3.5 w-3.5 animate-spin' />
                    ) : (
                      <ScanText className='me-1.5 h-3.5 w-3.5' />
                    )}
                    {t('startOcr')}
                  </Button>
                </PermissionGate>
              ) : (
                <Button
                  asChild
                  variant={status === 'completed' ? 'default' : 'outline'}
                  size='sm'
                  className='h-8'
                >
                  <Link href={`/dashboard/ocr-containers/${container.id}`}>
                    {status === 'processing' ? (
                      <Loader2 className='me-1.5 h-3.5 w-3.5 animate-spin' />
                    ) : (
                      <ExternalLink className='me-1.5 h-3.5 w-3.5' />
                    )}
                    {t('openResultsPage')}
                  </Link>
                </Button>
              )}

              {/* Secondary actions, collapsed to avoid icon clutter */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant='ghost' size='icon' className='h-8 w-8'>
                    <EllipsisVertical className='h-4 w-4' />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>
                  {/* Pause / resume the run. Mutually exclusive by status:
                      `processing` (which already covers pending) can be
                      paused, `paused` can be resumed. */}
                  {status === 'processing' && (
                    <PermissionGate permissions={['ocr.edit']}>
                      <DropdownMenuItem
                        onClick={() => handlePauseOcr(container)}
                        disabled={pauseMutation.isPending}
                      >
                        <Pause className='me-2 h-3.5 w-3.5' />
                        {t('pauseOcr')}
                      </DropdownMenuItem>
                    </PermissionGate>
                  )}
                  {status === 'processing' && (
                    <PermissionGate permissions={['ocr.edit']}>
                      <DropdownMenuItem
                        onClick={() => handleRecoverOcr(container)}
                        disabled={recoverMutation.isPending}
                      >
                        <LifeBuoy className='me-2 h-3.5 w-3.5' />
                        {t('recoverOcr')}
                      </DropdownMenuItem>
                    </PermissionGate>
                  )}
                  {status === 'paused' && (
                    <PermissionGate permissions={['ocr.edit']}>
                      <DropdownMenuItem
                        onClick={() => handleResumeOcr(container)}
                        disabled={resumeMutation.isPending}
                      >
                        <Play className='me-2 h-3.5 w-3.5' />
                        {t('resumeOcr')}
                      </DropdownMenuItem>
                    </PermissionGate>
                  )}

                  {hasPdf && (
                    <DropdownMenuItem onClick={() => handleViewPdf(container)}>
                      <FileStack className='me-2 h-3.5 w-3.5' />
                      {t('viewPdf')}
                    </DropdownMenuItem>
                  )}

                  {/* Move to repair queue — hidden once the container has
                      left the OCR stage for OCR-QA (repair is no longer an
                      OCR-stage action). */}
                  {container.registration?.id &&
                    !isInOcrQa(container.registration.current_stage) && (
                      <PermissionGate permissions={['ocr.edit']}>
                        <DropdownMenuItem
                          onClick={() => handleMoveToRepair(container)}
                          className='text-amber-600 focus:text-amber-600'
                        >
                          <AlertTriangle className='me-2 h-3.5 w-3.5' />
                          {tRepair('moveToRepair')}
                        </DropdownMenuItem>
                      </PermissionGate>
                    )}

                  {/* Repair-cycle history — only when the container was sent
                      at least once. */}
                  {container.repair_count > 0 && (
                    <DropdownMenuItem
                      onClick={() => handleViewRepairHistory(container)}
                    >
                      <History className='me-2 h-3.5 w-3.5' />
                      {t('viewRepairHistory')}
                    </DropdownMenuItem>
                  )}

                  {/* Navigate to container details page */}
                  {container.registration?.id && (
                    <RouteGate
                      route={`/dashboard/containers/${container.registration.id}`}
                    >
                      <DropdownMenuItem asChild>
                        <Link
                          href={`/dashboard/containers/${container.registration.id}`}
                        >
                          <Eye className='me-2 h-3.5 w-3.5' />
                          {t('viewContainer')}
                        </Link>
                      </DropdownMenuItem>
                    </RouteGate>
                  )}

                  {/* "View results" — the page-by-page summary sheet: per-page
                      status, thumbnails, suggestion history, and the rows that
                      open the extraction panel in-screen on a page or a field.
                      The row's primary action is the standalone results page;
                      this is the overview of a long run, without leaving the
                      list. */}
                  {!!status && (
                    <DropdownMenuItem
                      onClick={() => handleOpenResultsSummary(container)}
                    >
                      <FileText className='me-2 h-3.5 w-3.5' />
                      {t('viewResults')}
                    </DropdownMenuItem>
                  )}

                  {/* Delete — test containers only */}
                  {container.is_test && (
                    <PermissionGate permissions={['ocr.edit']}>
                      <DropdownMenuItem
                        onClick={() => handleDeleteTest(container)}
                        className='text-destructive focus:text-destructive'
                      >
                        <Trash2 className='me-2 h-3.5 w-3.5' />
                        {t('deleteTestContainer')}
                      </DropdownMenuItem>
                    </PermissionGate>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
        enableSorting: false,
        enableHiding: false
      }
    ],
    [
      t,
      tReg,
      tRepair,
      startMutation,
      pauseMutation,
      resumeMutation,
      recoverMutation,
      handleStartOcr,
      handleOpenResultsSummary,
      handleViewPdf,
      handlePauseOcr,
      handleResumeOcr,
      handleRecoverOcr,
      handleMoveToRepair,
      handleViewRepairHistory,
      handleDeleteTest
    ]
  );

  const filteredColumns = useFilteredColumns(columns);

  const { table } = useDataTable<OcrContainer>({
    columns: filteredColumns,
    pageKey: 'ocr-containers',
    data: containers,
    pageCount,
    initialState: {
      sorting: [{ id: 'created_at', desc: true }],
      pagination: { pageIndex: 0, pageSize: 10 }
    }
  });

  const sortOptions = useMemo(
    () => [{ label: t('date'), value: 'created_at' }],
    [t]
  );

  const tableState = table.state;

  return (
    <div className='space-y-6'>
      {/* Header controls */}
      <div className='flex items-center justify-between gap-2'>
        <div className='flex items-center gap-2'>
          {view !== 'table' && (
            <SortControl
              options={sortOptions}
              sorting={tableState.sorting}
              onSortingChange={(s) => table.setSorting(s)}
            />
          )}
          <DataViewToggle
            value={view}
            onValueChange={setView}
            pageKey='ocr-containers'
          />
        </div>
        <PermissionGate permissions={['ocr.edit']}>
          <Button variant='outline' onClick={() => toggleCreateTest(true)}>
            <FlaskConical className='me-2 h-4 w-4' />
            {t('createTestContainer')}
          </Button>
        </PermissionGate>
      </div>

      <DataTableToolbar
        table={table}
        viewFilterPermission='ocr.view_filter'
        infiniteConfigs={{ ocr_template_id: templateSelect }}
      />

      {isPending ? (
        <ListingSkeleton view={view} />
      ) : containers.length === 0 && tableState.columnFilters.length === 0 ? (
        <div className='flex flex-col items-center justify-center py-12 text-center'>
          <ScanText className='text-muted-foreground mb-4 h-12 w-12' />
          <p className='text-muted-foreground text-lg'>{t('noContainers')}</p>
        </div>
      ) : view === 'grid' ? (
        <>
          <div
            className={cn(
              'grid gap-4 sm:grid-cols-2 lg:grid-cols-3',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {containers.map((container) => (
              <ContainerCard
                key={container.id}
                container={container}
                isStarting={
                  startMutation.isPending &&
                  startMutation.variables?.containerId === container.id
                }
                onStartOcr={() => handleStartOcr(container)}
                onOpenResultsSummary={handleOpenResultsSummary}
                onViewPdf={handleViewPdf}
                onPauseOcr={handlePauseOcr}
                onResumeOcr={handleResumeOcr}
                onMoveToRepair={handleMoveToRepair}
                onViewRepairHistory={handleViewRepairHistory}
                onDeleteTest={handleDeleteTest}
              />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : view === 'compact' ? (
        <>
          <div
            className={cn(
              'space-y-2',
              isPlaceholderData && 'opacity-60 transition-opacity'
            )}
          >
            {containers.map((container) => (
              <ContainerCompactRow
                key={container.id}
                container={container}
                isStarting={
                  startMutation.isPending &&
                  startMutation.variables?.containerId === container.id
                }
                onStartOcr={() => handleStartOcr(container)}
                onOpenResultsSummary={handleOpenResultsSummary}
                onViewPdf={handleViewPdf}
                onPauseOcr={handlePauseOcr}
                onResumeOcr={handleResumeOcr}
                onMoveToRepair={handleMoveToRepair}
                onViewRepairHistory={handleViewRepairHistory}
                onDeleteTest={handleDeleteTest}
              />
            ))}
          </div>
          {pagination && pagination.totalPages > 0 && (
            <PaginationControls
              page={pagination.page}
              pageCount={pagination.totalPages}
              perPage={pagination.per_page}
              total={pagination.total}
              onPageChange={(p) => table.setPageIndex(p - 1)}
              onPerPageChange={(s) => {
                table.setPageSize(s);
                table.setPageIndex(0);
              }}
            />
          )}
        </>
      ) : (
        <div
          className={cn(
            'flex min-h-[500px] flex-col gap-2',
            isPlaceholderData && 'opacity-60 transition-opacity'
          )}
        >
          <DataTable table={table} />
        </div>
      )}

      {/* Results sheet — #results/<id> */}
      <ContainerResultsSheet
        containerId={resultsTarget ? resultsTarget.id : null}
        containerLabel={containerLabel(resultsTarget)}
        registrationId={resultsTarget?.registration?.id}
        onOpenResultsView={handleOpenResultsView}
        onClose={() => closeResults(false)}
      />

      {/* Extraction panel, in-screen — #results-view/<id>[/<target>] */}
      <ContainerResultsOverlay
        containerId={resultsViewTarget?.containerId ?? null}
        containerLabel={containerLabel(resultsViewContainer)}
        deepLink={resultsViewTarget?.deepLink ?? null}
        renderPageFooter={renderResultsPageFooter}
        onClose={() => closeResultsView(false)}
      />

      {/* Repair-cycle history — #history/<id> */}
      <ContainerRepairHistoryDialog
        open={repairHistoryOpen && !!repairHistoryTarget}
        onOpenChange={closeRepairHistory}
        containerLabel={containerLabel(repairHistoryTarget)}
        transfers={repairHistoryTarget?.registration?.container_transfers ?? []}
      />

      {/* Template pool picker — AI suggestions + manual candidate selection —
          #template-pool/<id> */}
      {poolTarget && (
        <OcrTemplatePoolDialog
          open={poolOpen && !!poolTarget}
          onOpenChange={closePool}
          containerId={poolTarget.containerId}
          registrationId={poolTarget.registrationId}
          containerLabel={poolTarget.label}
          batchId={poolTarget.batchId}
        />
      )}

      {/* PDF viewer dialog — #pdf/<batchId> */}
      <Dialog open={pdfBatchId !== null} onOpenChange={closePdf}>
        <DialogContent className='flex h-[90vh] flex-col gap-0 p-0 sm:max-w-4xl'>
          <DialogHeader className='border-b px-6 py-4'>
            <DialogTitle className='flex items-center gap-2 font-mono'>
              <FileText className='h-4 w-4' />
              {t('viewPdf')} — {pdfTitle}
            </DialogTitle>
          </DialogHeader>
          {pdfBatchId !== null && (
            <BatchPdfViewer
              key={pdfBatchId}
              batchId={pdfBatchId}
              pdfBlobUrl={pdfBlobUrl}
              title={pdfTitle}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Move to repair queue — #repair/<id> */}
      <MoveToRepairDialog
        open={refuseOpen && !!refuseTarget}
        onOpenChange={closeRefuse}
        containerNumber={containerLabel(refuseTarget)}
        onConfirm={handleConfirmRefuse}
        isPending={refuseMutation.isPending}
        optionalDestination={!!refuseTarget?.is_test}
      />

      {/* Ad-hoc "Test OCR" container creation — #create-test */}
      <CreateTestContainerDialog
        open={createTestOpen}
        onOpenChange={toggleCreateTest}
      />

      {/* Delete a test container — #delete/<id> */}
      <AlertDialog
        open={deleteTestOpen && !!deleteTestTarget}
        onOpenChange={closeDeleteTest}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteTestConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteTestConfirmBody', {
                name: containerLabel(deleteTestTarget)
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteTest}
              className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
            >
              {deleteTestMutation.isPending && (
                <Loader2 className='me-2 h-4 w-4 animate-spin' />
              )}
              {t('deleteTestContainer')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
